import { useDispatch, useSelector } from 'react-redux';
import { Cell, Text, Rating, Spacer, Image, Icon, Button, Divider } from '@vinted/web-ui';
import { HorizontalDots24, Copy24, Flag24 } from '@vinted/monochrome-icons';
import { FormattedDateMessage } from 'components/@support';
import DropdownMenu from 'components/DropdownMenu';
import { Translation } from 'constants/translation';
import { EDIT_FEEDBACK_URL, FEEDBACK_URL, INTELLECTUAL_PROPERTY_INFRINGEMENT } from 'constants/routes';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { FeedbackScreen } from 'state/user-feedback/constants';
import { actions } from 'state/user-feedback/slice';
import { getTranslatedFeedbackById, getFeedbackScreen } from 'state/user-feedback/selectors';
import useTranslate from 'hooks/useTranslate';
import useLocation from 'hooks/useLocation';
import FeedbackUsername from './FeedbackUsername';
import FeedbackThumbnail from './FeedbackThumbnail';
import FeedbackActions from './FeedbackActions';
import CommentInput from './CommentInput';
import Comment from './Comment';
import DeleteFeedbackModal from './DeleteFeedbackModal';
import FeedbackTranslationButton from './FeedbackTranslationButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const FeedbackItem = _ref => {
  let {
    id,
    userId,
    isContentTranslationEnabled
  } = _ref;
  const translate = useTranslate('user');
  const dispatch = useDispatch();
  const screen = useSelector(state => getFeedbackScreen(state, id));
  const {
    baseUrl
  } = useLocation();
  const isFeedbackReportingEnabled = useSelector(getIsFeatureSwitchEnabled('feedback_reporting'));
  const {
    user,
    isSystem,
    createdAt,
    rating,
    text,
    canReply,
    canChange,
    canDelete,
    comment,
    canChangeComment,
    canDeleteComment,
    localization
  } = useSelector(state => getTranslatedFeedbackById(state, id));

  const handleCopyClick = () => {
    navigator.clipboard.writeText("".concat(baseUrl).concat(FEEDBACK_URL(userId, id)));
  };

  const renderUsername = () => /*#__PURE__*/_jsx(FeedbackUsername, {
    user: user,
    isSystem: isSystem
  });

  const renderThumbnail = () => /*#__PURE__*/_jsx(FeedbackThumbnail, {
    user: user,
    size: Image.Size.Large,
    isSystem: isSystem
  });

  const renderDate = () => /*#__PURE__*/_jsx(FormattedDateMessage, {
    date: new Date(createdAt),
    relativeDate: true
  });

  const renderRating = () => /*#__PURE__*/_jsx(Rating, {
    value: rating,
    aria: {
      'aria-label': rating ? translate('a11y.rating', {
        rating,
        max_rating: 5
      }) : ''
    }
  });

  const renderFeedback = () => /*#__PURE__*/_jsx(Text, {
    children: text
  });

  const renderFeedbackWithDate = () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderFeedback(), /*#__PURE__*/_jsx(Spacer, {
      size: Spacer.Size.Small
    }), /*#__PURE__*/_jsx(Text, {
      type: Text.Type.Subtitle,
      theme: "muted",
      text: renderDate()
    })]
  });

  const renderActions = () => {
    return /*#__PURE__*/_jsx(FeedbackActions, {
      id: id,
      editFeedbackUrl: EDIT_FEEDBACK_URL(userId, id),
      canReply: canReply,
      canChange: canChange,
      canDelete: canDelete
    });
  };

  const renderComment = () => {
    switch (screen) {
      case FeedbackScreen.CreateComment:
      case FeedbackScreen.EditComment:
        return /*#__PURE__*/_jsx(CommentInput, {
          id: id
        });

      default:
        return comment && /*#__PURE__*/_jsx(Comment, {
          id: id,
          comment: comment,
          canChangeComment: canChangeComment,
          canDeleteComment: canDeleteComment
        });
    }
  };

  const handleFeedbackDelete = () => {
    switch (screen) {
      case FeedbackScreen.DeleteFeedback:
        dispatch(actions.feedbackDeleteRequest({
          feedbackId: id
        }));
        break;

      case FeedbackScreen.DeleteComment:
        dispatch(actions.feedbackCommentDeleteRequest({
          feedbackId: id
        }));
        break;

      default:
        break;
    }
  };

  const handleCancel = () => {
    dispatch(actions.feedbackToggleCancel({
      feedbackId: id
    }));
  };

  const renderDeleteFeedbackModal = () => /*#__PURE__*/_jsx(DeleteFeedbackModal, {
    isOpen: screen === FeedbackScreen.DeleteFeedback || screen === FeedbackScreen.DeleteComment,
    onSubmit: handleFeedbackDelete,
    onClose: handleCancel
  });

  const renderTranslationButton = () => {
    if (!isContentTranslationEnabled || !localization || localization === Translation.None) return null;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Spacer, {}), /*#__PURE__*/_jsx(FeedbackTranslationButton, {
        feedbackId: id
      })]
    });
  };

  const renderDropdownMenu = () => {
    const buttonProps = {
      icon: /*#__PURE__*/_jsx(Icon, {
        name: HorizontalDots24
      }),
      styling: Button.Styling.Flat
    };
    const dropdownActions = [{
      id: 'copy',
      text: /*#__PURE__*/_jsx(Text, {
        text: translate('feedback.copy'),
        theme: "amplified"
      }),
      prefix: /*#__PURE__*/_jsx(Icon, {
        name: Copy24
      }),
      onClick: handleCopyClick
    }, {
      id: 'report',
      text: /*#__PURE__*/_jsx(Text, {
        text: translate('feedback.report'),
        theme: "amplified"
      }),
      prefix: /*#__PURE__*/_jsx(Icon, {
        name: Flag24,
        color: Icon.Color.Warning
      }),
      url: INTELLECTUAL_PROPERTY_INFRINGEMENT
    }];
    return /*#__PURE__*/_jsx(DropdownMenu, {
      styling: Cell.Styling.Default,
      separator: /*#__PURE__*/_jsx(Divider, {}),
      actions: dropdownActions,
      buttonProps: buttonProps
    });
  };

  return /*#__PURE__*/_jsxs(Cell, {
    title: renderUsername(),
    prefix: renderThumbnail(),
    subtitle: isFeedbackReportingEnabled ? renderDropdownMenu() : renderDate(),
    styling: Cell.Styling.Tight,
    testId: "feedback-item",
    children: [renderRating(), /*#__PURE__*/_jsx(Spacer, {}), isFeedbackReportingEnabled ? renderFeedbackWithDate() : renderFeedback(), /*#__PURE__*/_jsx(Spacer, {}), renderActions(), /*#__PURE__*/_jsx(Spacer, {}), renderComment(), renderTranslationButton(), renderDeleteFeedbackModal()]
  });
};

export default FeedbackItem;